/* eslint-disable react-refresh/only-export-components --
 * Route configuration module: its public export is the `router` object, and the
 * small components here exist only to wrap lazy routes.
 */
import { createBrowserRouter } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import Layout from '@/shared/components/layout/Layout'
import ProtectedRoute from '@/features/auth/components/ProtectedRoute'
import RequirePermissionRoute from '@/features/auth/components/RequirePermissionRoute'
import LoginPage from '@/features/auth/components/LoginPage'
import LoadingSpinner from '@/shared/components/ui/LoadingSpinner'
import type { UserPermissions } from '@/features/auth/types'

const Dashboard                 = lazy(() => import('@/features/dashboard/pages/DashboardPage'))
const StatementsPage            = lazy(() => import('@/features/statements/pages/StatementsPage'))
const StatementGenerationPage   = lazy(() => import('@/features/statements/pages/StatementGenerationPage'))
const EmailsPage         = lazy(() => import('@/features/emails/pages/EmailsPage'))
const BlastEmailsPage    = lazy(() => import('@/features/blast-emails/pages/BlastEmailsPage'))
const PdfUploadPage      = lazy(() => import('@/features/pdf-upload/pages/PdfUploadPage'))
const PdfBatchDetailPage = lazy(() => import('@/features/pdf-upload/pages/PdfBatchDetailPage'))
const StmtTemplatesPage  = lazy(() => import('@/features/statement-templates/pages/StmtTemplatesPage'))
const EmailTemplatesPage = lazy(() => import('@/features/email-templates/pages/EmailTemplatesPage'))
const UsersPage          = lazy(() => import('@/features/users/pages/UsersPage'))
const LogsPage           = lazy(() => import('@/features/logs/pages/LogsPage'))
const ProfilePage        = lazy(() => import('@/features/auth/pages/ProfilePage'))

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <LoadingSpinner />
    </div>
  )
}

/**
 * Wraps a lazy page in Suspense and, when a permission is supplied, in a
 * fail-closed authorization guard. Gating lives here so a new route cannot be
 * added without an explicit decision about who may reach it.
 */
function wrap(Page: React.ComponentType, permission?: keyof UserPermissions) {
  const element = (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  )

  if (!permission) return element

  return (
    <RequirePermissionRoute permission={permission}>
      {element}
    </RequirePermissionRoute>
  )
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true,                      element: wrap(Dashboard) },
      { path: 'statements',               element: wrap(StatementsPage, 'canGenerateStatements') },
      { path: 'statements/:batchId',        element: wrap(StatementGenerationPage, 'canGenerateStatements') },
      { path: 'emails',                   element: wrap(EmailsPage, 'canDraftEmails') },
      { path: 'blast-emails',             element: wrap(BlastEmailsPage, 'canSendEmails') },
      { path: 'pdf-upload',               element: wrap(PdfUploadPage, 'canUploadFiles') },
      { path: 'pdf-upload/:batchId',      element: wrap(PdfBatchDetailPage, 'canUploadFiles') },
      { path: 'statement-templates',      element: wrap(StmtTemplatesPage, 'canGenerateStatements') },
      { path: 'email-templates',          element: wrap(EmailTemplatesPage, 'canDraftEmails') },
      { path: 'users',                    element: wrap(UsersPage, 'canManageUsers') },
      { path: 'logs',                     element: wrap(LogsPage, 'canManageUsers') },
      // No permission guard: every signed-in user may view their own profile.
      { path: 'profile',                  element: wrap(ProfilePage) },
    ],
  },
])
